import type {
  IDocRecognitionConfig,
  IDocRecognitionConnection,
  IDocRecognitionDocumentDetail,
  IDocRecognitionDocumentListResponse,
  IDocRecognitionFieldEdit,
  IDocRecognitionLockState,
  IListDocRecognitionDocumentsParams,
  IUpdateDocRecognitionDocumentPayload,
  IUpsertDocRecognitionConfigPayload,
} from "../types";
import { encodePathSegment } from "../utils/encode-path-segment";
import type { ApiClient } from "./client";

// NOTE: NSAI-4317 — the review queue and configuration endpoints.
export class DocRecognitionApi {
  constructor(private readonly apiClient: ApiClient) {}

  async listDocuments(
    params: IListDocRecognitionDocumentsParams,
  ): Promise<IDocRecognitionDocumentListResponse> {
    const response =
      await this.apiClient.get<IDocRecognitionDocumentListResponse>(
        "/doc-recognition/documents",
        { params },
      );
    return response.data;
  }

  async getDocument(uuid: string): Promise<IDocRecognitionDocumentDetail> {
    const response = await this.apiClient.get<IDocRecognitionDocumentDetail>(
      `/doc-recognition/documents/${encodePathSegment(uuid)}`,
    );
    return response.data;
  }

  // NOTE: The original file, fetched as a Blob rather than pointed at directly from
  // an <object src>. It has to go through this client: the endpoint sits behind
  // JwtAuthGuard and is project-scoped, so it needs both the Authorization bearer
  // and the X-Project-Uuid header that the client's request interceptor attaches. A
  // browser loading `/api/...` in an <object> or <img> sends neither and gets a 401,
  // which is exactly how the preview used to fail.
  async getDocumentFile(uuid: string): Promise<Blob> {
    const response = await this.apiClient.get<Blob>(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/file`,
      { responseType: "blob" },
    );
    return response.data;
  }

  async getDocumentAudit(uuid: string): Promise<IDocRecognitionFieldEdit[]> {
    const response = await this.apiClient.get<IDocRecognitionFieldEdit[]>(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/audit`,
    );
    return response.data;
  }

  async claimDocument(uuid: string): Promise<IDocRecognitionLockState> {
    const response = await this.apiClient.post<IDocRecognitionLockState>(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/claim`,
    );
    return response.data;
  }

  // NOTE: Called on a timer by the open review form so an actively worked document
  // never expires under its reviewer.
  async heartbeatDocument(uuid: string): Promise<IDocRecognitionLockState> {
    const response = await this.apiClient.post<IDocRecognitionLockState>(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/heartbeat`,
    );
    return response.data;
  }

  // NOTE: POST, not DELETE — the platform convention (code-style-guide §7) is POST
  // for every mutation with the action in the URL path.
  async releaseDocumentLock(uuid: string): Promise<void> {
    await this.apiClient.post(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/lock/release`,
    );
  }

  async forceReleaseDocumentLock(uuid: string): Promise<void> {
    await this.apiClient.post(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/lock/force-release`,
    );
  }

  async updateDocument({
    uuid,
    payload,
  }: {
    uuid: string;
    payload: IUpdateDocRecognitionDocumentPayload;
  }): Promise<void> {
    await this.apiClient.post(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/update`,
      payload,
    );
  }

  async completeDocument(uuid: string): Promise<void> {
    await this.apiClient.post(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/complete`,
    );
  }

  async rejectDocument(uuid: string): Promise<void> {
    await this.apiClient.post(
      `/doc-recognition/documents/${encodePathSegment(uuid)}/reject`,
    );
  }

  // NOTE: Null when the organization has not configured the feature yet — the
  // settings screen shows an empty form rather than treating it as an error.
  async getConfig(): Promise<IDocRecognitionConfig | null> {
    const response = await this.apiClient.get<IDocRecognitionConfig | null>(
      "/doc-recognition/config",
    );
    return response.data;
  }

  async getAvailableConnections(): Promise<IDocRecognitionConnection[]> {
    const response = await this.apiClient.get<IDocRecognitionConnection[]>(
      "/doc-recognition/config/connections",
    );
    return response.data;
  }

  async upsertConfig(
    payload: IUpsertDocRecognitionConfigPayload,
  ): Promise<IDocRecognitionConfig> {
    const response = await this.apiClient.post<IDocRecognitionConfig>(
      "/doc-recognition/config/update",
      payload,
    );
    return response.data;
  }

  async deleteConfig(): Promise<void> {
    await this.apiClient.post("/doc-recognition/config/delete");
  }
}
